import type { EditorContext, EditorRegion } from "@loumy/contracts";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getEditorCore, listEditors, type WebPanelDescriptor } from "../registry/editorRegistry";
import { cx, Icon, Spinner } from "../ui";
import { DEFAULT_LAYOUT, layoutFromHints, useEditorStore } from "./editorStore";
import { EditorMenuStrip } from "./EditorMenuStrip";
import { COLLAPSED_PX, RegionContainer } from "./RegionContainer";
import { ResizeHandle } from "./ResizeHandle";
import { useEditorUI } from "./useEditorUI";

// §11/D66 — Editor-Surface (eigene innere Shell, geroutet unter /editor). Festes 5-Regionen-
// Layout: Toolbar oben, Tools links, Viewport mitte, rechts Outliner (oben) + Details (unten).
// Resizable (Eigenbau-Splitter) + ein/ausklappbar; Layout je Editor persistiert (localStorage).
// Vollständig registry-getrieben: „Nichts referenziert einen Editor hart" — Auswahl und Panels
// kommen aus listEditors()/useEditorUI(). Die äußere AppShell (Rail/Backbone) bleibt unberührt.

const EMPTY_PANELS: Record<EditorRegion, WebPanelDescriptor[]> = {
  toolbar: [],
  tools: [],
  viewport: [],
  outliner: [],
  details: [],
};

export function EditorShell() {
  const { editorType } = useParams();
  const navigate = useNavigate();

  const editors = useMemo(() => listEditors(), []);
  const storeActive = useEditorStore((s) => s.activeEditorType);
  const activeId = editorType ?? storeActive ?? editors[0]?.id ?? null;

  const core = activeId ? getEditorCore(activeId) : undefined;
  const { ui, loading } = useEditorUI(core ? activeId ?? undefined : undefined);

  // Store-Slices für den aktiven Editor.
  const doc = useEditorStore((s) => (activeId ? s.docs[activeId] : undefined));
  const uiState = useEditorStore((s) => (activeId ? s.uiStates[activeId] : undefined));
  const selection = useEditorStore((s) => s.selection);
  const layout = useEditorStore((s) => (activeId ? s.layouts[activeId] : undefined)) ?? DEFAULT_LAYOUT;
  const setDoc = useEditorStore((s) => s.setDoc);
  const setUi = useEditorStore((s) => s.setUi);
  const setSelection = useEditorStore((s) => s.setSelection);
  const resizeRegion = useEditorStore((s) => s.resizeRegion);
  const commitLayout = useEditorStore((s) => s.commitLayout);
  const toggleRegion = useEditorStore((s) => s.toggleRegion);
  const initEditor = useEditorStore((s) => s.initEditor);
  const setActiveEditor = useEditorStore((s) => s.setActiveEditor);

  // Aktiven Editor registrieren + idempotent initialisieren (doc/ui/layout) beim Mount/Wechsel.
  useEffect(() => {
    if (!activeId || !core) return;
    setActiveEditor(activeId);
    initEditor(activeId, {
      doc: core.createDocument(),
      ui: core.createUiState ? core.createUiState() : {},
      layout: layoutFromHints(core.layoutHints),
    });
  }, [activeId, core, setActiveEditor, initEditor]);

  // EditorContext (D66) — reicht den GESAMTEN State an jedes Panel. requestExport bleibt im
  // Framework-MVP undefined (Toolbar zeigt einen deaktivierten Export-Hinweis, D51).
  const ctx: EditorContext = useMemo(
    () => ({
      doc,
      setDoc: (next) => {
        if (activeId) setDoc(activeId, next);
      },
      ui: uiState,
      setUi: (next) => {
        if (activeId) setUi(activeId, next);
      },
      selection,
      setSelection,
      // Shell-Panel-Steuerung für editor-eigene „Ansicht/Fenster"-Menüs (D66) — der Editor
      // togglet Regionen, ohne den Shell-Store zu kennen. Die toolbar selbst bleibt absichtlich
      // außen vor (sie trägt das Menü — ein Ausblenden würde den Wiedereinblend-Weg sperren).
      panels: {
        isVisible: (region) => !(layout[region] ?? DEFAULT_LAYOUT[region]).collapsed,
        toggle: (region) => {
          if (activeId && region !== "toolbar" && region !== "viewport") toggleRegion(activeId, region);
        },
      },
    }),
    [doc, uiState, selection, activeId, layout, setDoc, setUi, setSelection, toggleRegion],
  );

  const byRegion = useMemo(() => {
    const map: Record<EditorRegion, WebPanelDescriptor[]> = {
      toolbar: [],
      tools: [],
      viewport: [],
      outliner: [],
      details: [],
    };
    for (const p of ui?.panels ?? []) map[p.region].push(p);
    return map;
  }, [ui]);

  // Layout-Helfer: kollabierte Region defensiv gegen fehlende Keys.
  const lay = (r: EditorRegion) => layout[r] ?? DEFAULT_LAYOUT[r];
  // Collapsibility aus den Editor-Hints (Default: erlaubt) — respektiert den Contract.
  const collapsibleOf = (r: EditorRegion) => core?.layoutHints?.[r]?.collapsible ?? true;

  const panels = ui ? byRegion : EMPTY_PANELS;
  const outlinerC = lay("outliner").collapsed;
  const detailsC = lay("details").collapsed;
  const rightCollapsed = outlinerC && detailsC; // beide zu → die ganze Spalte schrumpft
  // Höhenverteilung der rechten Spalte: wer „flext" (nimmt die Rest-Höhe) vs. fixe Höhe.
  const outlinerFlex = rightCollapsed || !outlinerC;
  const detailsFlex = rightCollapsed || (!detailsC && outlinerC);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-bg">
      {/* Shell-Chrome: registry-getriebene Editor-Auswahl (kein hartes Editor-Ref, §1/§4). */}
      <div className="flex shrink-0 items-center gap-1 border-b border-border bg-surface px-2 py-1.5">
        <span className="px-1.5 text-[11px] font-medium uppercase tracking-wide text-muted">Editor</span>
        {editors.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => navigate(`/editor/${e.id}`)}
            className={cx(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              e.id === activeId ? "bg-accent/20 text-accent" : "text-muted hover:text-text",
            )}
          >
            <Icon name={e.icon.name} size={14} />
            {e.label}
          </button>
        ))}
      </div>

      {/* D87 — geteilte Menü-Leiste direkt unter den Tabs: Datei (Projekt-Quellen D69) | editor-Menüs
          (Bearbeiten/Ansicht via ui.menus) | Level (Multi-Doc-Host D72), rechts der Projekt·Level-Status.
          Editor- + Shell-Menüs werden in EditorMenuStrip per Label gemerged. Editor-Menüs erst berechnen,
          wenn doc/uiState initialisiert sind (Codex-P1: ein Menü, das ctx.doc/ctx.ui liest, darf nicht
          gegen undefined laufen) — die Shell-Menüs (Datei/Level) brauchen nur den Host und erscheinen sofort. */}
      {core && (
        <EditorMenuStrip
          core={core}
          ctx={ctx}
          editorMenus={ui && doc !== undefined && uiState !== undefined ? (ui.menus?.(ctx) ?? []) : []}
        />
      )}

      {/* Inhalt: Editor-spezifisches Layout, leerer/ladender Zustand */}
      {!core ? (
        <EmptyState
          icon="canvas"
          title="Kein Editor gewählt"
          note={editors.length ? "Wähle oben einen Editor." : "Es ist kein Editor registriert (§13)."}
        />
      ) : loading || doc === undefined || uiState === undefined ? (
        <div className="grid flex-1 place-items-center text-muted">
          <Spinner size={22} />
        </div>
      ) : !ui ? (
        <EmptyState icon="tools" title={core.label} note="Für diesen Editor ist keine UI registriert." />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar (oben, feste Höhe) — seit D87 NUR die werkzeug-spezifische Tool-Settings-Bar
              (Snapping/Modus/Form); die Menüleiste lebt shell-global in der EditorMenuStrip unter den Tabs.
              relative+z, damit etwaige Tool-Dropdowns über dem Body liegen (nicht abgeschnitten werden). */}
          {panels.toolbar.length > 0 && (
            <div style={{ height: lay("toolbar").size }} className="relative z-20 shrink-0 border-b border-border">
              <RegionContainer
                region="toolbar"
                orientation="height"
                panels={panels.toolbar}
                ctx={ctx}
                collapsed={false}
                collapsible={false}
                onToggle={() => {}}
                headerless
                allowOverflow
              />
            </div>
          )}

          {/* Body: Tools | Viewport | (Outliner / Details) */}
          <div className="flex min-h-0 flex-1">
            {/* Tools */}
            <div
              style={{ width: lay("tools").collapsed ? COLLAPSED_PX : lay("tools").size }}
              className="shrink-0 border-r border-border"
            >
              <RegionContainer
                region="tools"
                orientation="width"
                panels={panels.tools}
                ctx={ctx}
                collapsed={lay("tools").collapsed}
                collapsible={collapsibleOf("tools")}
                onToggle={() => activeId && toggleRegion(activeId, "tools")}
              />
            </div>
            {!lay("tools").collapsed && (
              <ResizeHandle
                axis="x"
                getValue={() => lay("tools").size}
                onResize={(n) => activeId && resizeRegion(activeId, "tools", n)}
                onCommit={commitLayout}
                min={140}
                max={420}
                label="Werkzeug-Breite ändern"
              />
            )}

            {/* Viewport (Rest-Fläche, headerless, randlos) */}
            <div className="relative min-w-0 flex-1">
              <RegionContainer
                region="viewport"
                orientation="height"
                panels={panels.viewport}
                ctx={ctx}
                collapsed={false}
                collapsible={false}
                onToggle={() => {}}
                fill
                headerless
              />
            </div>

            {/* Splitter Viewport ↔ rechte Spalte (nur wenn die Spalte volle Breite hat).
                max großzügig (D102): die Spalte trägt jetzt auch den Logic-Editor (Blockly). */}
            {!rightCollapsed && (
              <ResizeHandle
                axis="x"
                sign={-1}
                getValue={() => lay("outliner").size}
                onResize={(n) => activeId && resizeRegion(activeId, "outliner", n)}
                onCommit={commitLayout}
                min={200}
                max={880}
                label="Seitenleiste-Breite ändern"
              />
            )}
            <div
              style={{ width: rightCollapsed ? COLLAPSED_PX : lay("outliner").size }}
              className="flex shrink-0 flex-col border-l border-border"
            >
              {/* Outliner (oben) */}
              <div
                className={cx("overflow-hidden", outlinerFlex ? "min-h-0 flex-1" : "shrink-0")}
                style={outlinerFlex ? undefined : { height: COLLAPSED_PX }}
              >
                <RegionContainer
                  region="outliner"
                  orientation={rightCollapsed ? "width" : "height"}
                  panels={panels.outliner}
                  ctx={ctx}
                  collapsed={outlinerC}
                  collapsible={collapsibleOf("outliner")}
                  onToggle={() => activeId && toggleRegion(activeId, "outliner")}
                />
              </div>
              {/* Splitter Outliner ↔ Details (nur wenn beide offen) */}
              {!outlinerC && !detailsC && (
                <ResizeHandle
                  axis="y"
                  sign={-1}
                  getValue={() => lay("details").size}
                  onResize={(n) => activeId && resizeRegion(activeId, "details", n)}
                  onCommit={commitLayout}
                  min={120}
                  max={520}
                  label="Details-Höhe ändern"
                />
              )}
              {/* Details (unten) */}
              <div
                className={cx("overflow-hidden border-t border-border", detailsFlex ? "min-h-0 flex-1" : "shrink-0")}
                style={detailsFlex ? undefined : { height: detailsC ? COLLAPSED_PX : lay("details").size }}
              >
                <RegionContainer
                  region="details"
                  orientation={rightCollapsed ? "width" : "height"}
                  panels={panels.details}
                  ctx={ctx}
                  collapsed={detailsC}
                  collapsible={collapsibleOf("details")}
                  onToggle={() => activeId && toggleRegion(activeId, "details")}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon, title, note }: { icon: string; title: string; note: string }) {
  return (
    <div className="grid flex-1 place-items-center p-8 text-center">
      <div className="max-w-sm space-y-2 text-muted">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl2 bg-surface-2 text-muted">
          <Icon name={icon} size={24} />
        </span>
        <h2 className="text-sm font-medium text-text">{title}</h2>
        <p className="text-xs">{note}</p>
      </div>
    </div>
  );
}
